import {
  GOOGLE_CHAT_APP_SPACE_PATH_REGEX,
  GOOGLE_CHAT_SPACE_NAME_REGEX,
  GOOGLE_CHAT_SPACE_URL_REGEX,
} from 'src/constants/Regex';
import {
  GOOGLE_CHAT_ROOM_PREFIX,
  GOOGLE_CHAT_SPACE_PREFIX,
  GOOGLE_CHAT_SPACES_PREFIX,
} from 'src/constants/googleChat';
import { EMPTY_STRING } from 'src/constants/utils';
import { GOOGLE_CHAT_LINK } from 'src/constants/Urls';

/**
 * Google account slots are browser-session-specific, not Hana user identities.
 * Route authenticated links by email while retaining the exact room/hash. Never
 * add an account identifier to a non-Google URL supplied by a provider record.
 */
export const withGoogleChatAccount = (
  destination: string,
  email?: string | null,
): string => {
  const account = email?.trim();

  if (!account) return destination;

  try {
    const url = new URL(destination);

    if (
      url.protocol !== 'https:' ||
      !['mail.google.com', 'chat.google.com'].includes(url.hostname) ||
      url.username ||
      url.password ||
      url.port
    ) {
      return destination;
    }

    // An explicit /u/0/ path can override account selection. Remove only the
    // provider account-slot segment, not the room path or message anchor.
    url.pathname = url.pathname.replace(/\/u\/\d+(?=\/|$)/, '');
    url.searchParams.set('authuser', account);

    return url.toString();
  } catch {
    return destination;
  }
};

export const getGoogleChatHomeUrl = (email?: string | null): string =>
  email?.trim()
    ? withGoogleChatAccount('https://chat.google.com/', email)
    : GOOGLE_CHAT_LINK;

export const extractSpaceIdFromUrl = (url: string): string => {
  // Current Google Chat uses /app/chat/:id, optionally with a browser account
  // slot. Parse only the real HTTPS provider route; a space name or an unrelated
  // website containing this path must not turn into a provider resource ID.
  try {
    const destination = new URL(url);
    const appSpace = destination.pathname.match(
      GOOGLE_CHAT_APP_SPACE_PATH_REGEX,
    );

    if (
      destination.protocol === 'https:' &&
      destination.hostname === 'chat.google.com' &&
      !destination.username &&
      !destination.password &&
      !destination.port &&
      appSpace?.[1]
    ) {
      return `${GOOGLE_CHAT_SPACES_PREFIX}${appSpace[1]}`;
    }
  } catch {
    // Canonical resource names and ordinary search terms use the legacy path.
  }

  const match = url.match(GOOGLE_CHAT_SPACE_URL_REGEX);

  if (!match) {
    return url;
  }

  const [capturedSpaceId] = match;

  if (capturedSpaceId.startsWith(GOOGLE_CHAT_SPACES_PREFIX)) {
    return capturedSpaceId;
  }

  return capturedSpaceId
    .replace(GOOGLE_CHAT_SPACE_PREFIX, GOOGLE_CHAT_SPACES_PREFIX)
    .replace(GOOGLE_CHAT_ROOM_PREFIX, GOOGLE_CHAT_SPACES_PREFIX);
};

export const normalizeGoogleChatSpaceSearchQuery = (
  inputValue: string,
): string => {
  const trimmedValue = inputValue.trim();

  if (!trimmedValue) {
    return EMPTY_STRING;
  }

  return extractSpaceIdFromUrl(trimmedValue);
};

export const buildGoogleChatSpaceUrl = (spaceId: string): string | null => {
  const normalizedSpaceId = extractSpaceIdFromUrl(spaceId.trim());

  if (!GOOGLE_CHAT_SPACE_NAME_REGEX.test(normalizedSpaceId)) {
    return null;
  }

  const chatSpaceId = normalizedSpaceId.replace(GOOGLE_CHAT_SPACES_PREFIX, '');

  return `https://mail.google.com/mail/u/0/#chat/space/${chatSpaceId}`;
};

export const resolveGoogleChatSpaceUrl = (
  spaceUri?: string | null,
  spaceId?: string | null,
  email?: string | null,
): string => {
  const canonicalSpaceUrl = spaceId ? buildGoogleChatSpaceUrl(spaceId) : null;

  if (canonicalSpaceUrl) {
    const normalizedUriSpaceId = spaceUri
      ? extractSpaceIdFromUrl(spaceUri)
      : null;

    if (
      normalizedUriSpaceId &&
      normalizedUriSpaceId === extractSpaceIdFromUrl(spaceId as string)
    ) {
      return withGoogleChatAccount(spaceUri as string, email);
    }

    return withGoogleChatAccount(canonicalSpaceUrl, email);
  }

  return spaceUri
    ? withGoogleChatAccount(spaceUri, email)
    : getGoogleChatHomeUrl(email);
};
